<template>
  <div class="address-input">
    <slot name="header-text"/>
    <p class="address-input__name" v-text="name"/>

    <AddressAutocomplete @placeChanged="onPlaceChanged"/>

    <SmoothReflow :show="meta.manualMode">
      <div class="row">
        <div class="col-sm-6">
          <FormGroup :error-container="errorContainer" :name="errorPrefixedKeys.unit_number"
                     label="Unit Number" :floating="true">
            <input :value="proxyValue.unit_number" class="form-control" type="text" placeholder=" "
                   @input="setPlaceField('unit_number', $event)">
          </FormGroup>
        </div>

        <div class="col-sm-6">
          <FormGroup :error-container="errorContainer" :name="errorPrefixedKeys.street_number"
                     label="Street Number" :floating="true">
            <input :value="proxyValue.street_number" class="form-control" type="text" placeholder=" "
                   @input="setPlaceField('street_number', $event)">
          </FormGroup>
        </div>

        <div class="col-sm-8">
          <FormGroup :error-container="errorContainer" :name="errorPrefixedKeys.street_name"
                     label="Street Name" :floating="true">
            <input :value="proxyValue.street_name" class="form-control" type="text" placeholder=" "
                   @input="setPlaceField('street_name', $event)">
          </FormGroup>
        </div>

        <div class="col-sm-4">
          <FormGroup :error-container="errorContainer" :name="errorPrefixedKeys.suburb"
                     label="Suburb" :floating="true">
            <input :value="proxyValue.suburb" class="form-control" type="text" placeholder=" "
                   @input="setPlaceField('suburb', $event)">
          </FormGroup>
        </div>

        <div class="col-sm-4">
          <FormGroup :error-container="errorContainer" :name="errorPrefixedKeys.state"
                     label="State" :floating="true">
            <input :value="proxyValue.state" class="form-control" type="text" placeholder=" "
                   @input="setPlaceField('state', $event)">
          </FormGroup>
        </div>

        <div class="col-sm-4">
          <FormGroup :error-container="errorContainer" :name="errorPrefixedKeys.postcode"
                     label="Postcode" :floating="true">
            <input :value="proxyValue.postcode" class="form-control" type="text" placeholder=" "
                   @input="setPlaceField('postcode', $event)">
          </FormGroup>
        </div>

        <div class="col-sm-4">
          <CountrySelect
            :value="proxyValue.country"
            value-by="name"
            :error-container="errorContainer"
            :name="errorPrefixedKeys.country"
            label="Country"
            :floating="true"
            @input="setRawField('country', $event)"
          />
        </div>
      </div>
    </SmoothReflow>

    <template v-if="duration">
      <p class="address-input__duration-label">How long have you lived at this address?</p>
      <div class="row">
        <div class="col-md-6">
          <FormGroup :error-container="errorContainer" :name="errorPrefixedKeys.years"
                     label="Number of years" :floating="true">
            <input :value="proxyValue.years" class="form-control" min="0" name="number_of_years"
                   step="1" type="number" placeholder=" "
                   @input="setPlaceField('years', $event)"/>
          </FormGroup>
        </div>
        <div class="col-md-6">
          <FormGroup :error-container="errorContainer" :name="errorPrefixedKeys.months"
                     label="Number of months" :floating="true">
            <FmSelect
              :value="proxyValue.months"
              :options="monthOptions"
              :icons="monthIcons"
              @input="setRawField('months', $event)"
            />
          </FormGroup>
        </div>
      </div>
    </template>

    <slot name="extra-text"/>
  </div>
</template>

<script>
import FormGroup from '@modules/shared/components/form/FormGroup';
import FmSelect from '@modules/shared/components/form/FmSelect';
import CountrySelect from '@modules/shared/components/user/CountrySelect';
import AddressAutocomplete from '@modules/shared/components/google/AddressAutocomplete';
import SmoothReflow from '@modules/shared/transitions/SmoothReflow';
import {mergeDeep} from '@modules/shared/helpers/object-fns';
import ServerErrorMixin from '@modules/shared/mixins/ServerErrorMixin';

const CLOCK_ICON = `<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16"><path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z"/><path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z"/></svg>`;

export default {
  name: 'AddressInput',
  mixins: [ServerErrorMixin],
  components: { SmoothReflow, AddressAutocomplete, FormGroup, FmSelect, CountrySelect },
  props: {
    value: {
      type: Object,
      required: false,
      default: () => ({
        unit_number: null, street_number: null, street_name: null, suburb: null,
        state: null, country: null, postcode: null, years: null, months: null,
      }),
    },
    name:           { type: String,  default: 'Address' },
    removable:      { type: Boolean, default: false },
    duration:       { type: Boolean, default: true },
    errorContainer: { type: String,  default: null },
    errorKeyPrefix: { type: String,  default: '' },
  },
  data() {
    return { meta: { manualMode: true } };
  },
  computed: {
    proxyValue: {
      get() { return this.value; },
      set(value) {
        if (this.duration) {
          this.$emit('input', value);
          return;
        }
        const newValue = {...value};
        delete newValue.years;
        delete newValue.months;
        this.$emit('input', newValue);
      },
    },
    errorPrefixedKeys() {
      const p = this.errorKeyPrefix;
      return {
        unit_number: `${p}unit_number`, street_number: `${p}street_number`,
        street_name: `${p}street_name`, suburb: `${p}suburb`,
        state: `${p}state`, country: `${p}country`,
        postcode: `${p}postcode`, years: `${p}years`, months: `${p}months`,
      };
    },
    isInvalid() {
      return Object.values(this.errorPrefixedKeys).some(key => this.validationErrors[key] ?? false);
    },
    numberOfMonths: () => [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
    monthOptions() {
      return Object.fromEntries(
        this.numberOfMonths.map(n => [n, n === 1 ? '1 month' : `${n} months`])
      );
    },
    monthIcons() {
      return Object.fromEntries(this.numberOfMonths.map(n => [n, CLOCK_ICON]));
    },
  },
  watch: {
    errorContainer: {
      handler(value) { if (value) this.registerErrorContainer(value); },
      immediate: true,
    },
    isInvalid(isInvalid) {
      if (isInvalid) this.meta.manualMode = true;
    },
  },
  methods: {
    onPlaceChanged(place) {
      this.proxyValue = mergeDeep({...this.proxyValue}, place);
      this.meta.manualMode = true;
    },
    setPlaceField(key, event) {
      this.proxyValue = {...this.proxyValue, [key]: event?.target?.value ?? null};
    },
    setRawField(key, value) {
      this.proxyValue = {...this.proxyValue, [key]: value};
    },
  },
};
</script>

<style lang="scss" scoped>
.address-input {
  background: #FAFAF8;
  border: 1.5px solid #E7E4DA;
  border-radius: 14px;
  padding: 1.25rem 1.25rem 0.25rem;
  margin-bottom: 1.5rem;

  &__name {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #9197A1;
    margin-bottom: 1rem;
    margin-top: 0;
  }

  &__duration-label {
    font-size: 13px;
    font-weight: 600;
    color: #5B606B;
    margin-bottom: 0.5rem;
    margin-top: 0.25rem;
  }
}
</style>
